import React, { memo } from 'react';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-common/src/async.tsx';
import {
	setPerformanceMarkLoadStart,
	setPerformanceMarkLoadEnd,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/performance-marks/index.tsx';
import { lazyAfterPaint, lazyForPaint } from '@atlassian/react-loosely-lazy';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import type { SidebarNextGen as SoftwareProjectSidebarType } from './ui/index.tsx';

const LazyAfterPaintClassicSoftwareProjectSidebar = lazyAfterPaint<
	typeof SoftwareProjectSidebarType
>(
	() => {
		setPerformanceMarkLoadStart();
		return import(/* webpackChunkName: "async-sidebar-next-gen" */ './index').then((bundle) => {
			setPerformanceMarkLoadEnd();
			return bundle;
		});
	},
	{ entryFsSsrLiftImportToModule: false },
);

const LazyForPaintClassicSoftwareProjectSidebar = lazyForPaint<typeof SoftwareProjectSidebarType>(
	() => {
		setPerformanceMarkLoadStart();
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		return import(/* webpackChunkName: "async-sidebar-next-gen" */ './index').then((bundle) => {
			setPerformanceMarkLoadEnd();
			return bundle;
		});
	},
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: false,
	},
);

const AfterPaintSoftwareProjectSidebar = memo(() => (
	<SidebarBoundary id="softwareProject">
		<LazyAfterPaintClassicSoftwareProjectSidebar />
	</SidebarBoundary>
));

const ForPaintSoftwareProjectSidebar = memo(() => (
	<SidebarBoundary id="softwareProject">
		<LazyForPaintClassicSoftwareProjectSidebar />
	</SidebarBoundary>
));

const SoftwareProjectSidebar = memo(() => {
	const isIssueRoute = useIsActiveRoute('issue');
	return isIssueRoute ? <ForPaintSoftwareProjectSidebar /> : <AfterPaintSoftwareProjectSidebar />;
});

SoftwareProjectSidebar.displayName = 'SoftwareProjectSidebar';
ForPaintSoftwareProjectSidebar.displayName = 'SoftwareProjectSidebar';

export { ForPaintSoftwareProjectSidebar };
export default SoftwareProjectSidebar;
